import React, { useState } from 'react'
import { fmtMoney } from '../../utils/formatters'
import { buildIngroundProposal } from '../../utils/proposal'

var SHAPES = ['rectangle', 'l_shape', 'oval', 'grecian', 'freeform']
var SHAPE_LABELS = { rectangle: 'RECTANGLE', l_shape: 'L-SHAPE', oval: 'OVAL', grecian: 'GRECIAN', freeform: 'FREEFORM' }

var VERM_RECT = [
  { size: '12x24', bags: 24 }, { size: '14x28', bags: 33 }, { size: '15x24', bags: 31 },
  { size: '16x24', bags: 33 }, { size: '16x32', bags: 45 }, { size: '16x34', bags: 46 },
  { size: '17x33', bags: 48 }, { size: '17x37', bags: 53 }, { size: '18x24', bags: 37 },
  { size: '18x36', bags: 56 }, { size: '20x40', bags: 68 }, { size: '21x41', bags: 74 },
  { size: '25x50', bags: 107 }, { size: '30x60', bags: 153 },
]

var VERM_LSHAPE = [
  { size: '16x36x24', bags: 65 }, { size: '16x40x20', bags: 61 },
  { size: '20x44x36', bags: 104 }, { size: '20x46x24', bags: 87 },
]

var DEFAULT_EXCLUSIONS = [
  { label: 'Fence', rate: '87', unit: 'per ft', included: false, custom_note: '' },
  { label: 'Concrete Patio', rate: '10', unit: 'per sqft', included: false, custom_note: '' },
  { label: 'Electrical', rate: '', unit: '', included: false, custom_note: 'by homeowner' },
  { label: 'Gas Line', rate: '', unit: '', included: false, custom_note: 'by homeowner' },
  { label: 'Permits', rate: '', unit: '', included: false, custom_note: 'by homeowner' },
  { label: 'Landscaping / Sod Repair', rate: '', unit: '', included: false, custom_note: '' },
  { label: 'Decking', rate: '', unit: '', included: false, custom_note: '' },
]

function calcPerimeter(shape, dims) {
  var l = parseFloat(dims.length) || 0
  var w = parseFloat(dims.width) || 0
  switch (shape) {
    case 'rectangle': return 2 * (l + w)
    case 'l_shape': return calcLShapePerimeter(dims)
    case 'oval':
      if (l > w) return 2 * (l - w) + Math.PI * w
      return Math.PI * l
    case 'grecian': return 2 * (l + w) * 0.94
    case 'freeform': return parseFloat(dims.perimeter) || 0
    default: return 2 * (l + w)
  }
}

function calcLShapePerimeter(dims) {
  var mL = parseFloat(dims.length) || 0
  var mW = parseFloat(dims.width) || 0
  var eL = parseFloat(dims.length2) || 0
  var eW = parseFloat(dims.width2) || 0
  return mL + mW + eL + eW + Math.abs(mL - eL) + Math.abs(mW - eW)
}

function calcArea(shape, dims) {
  var l = parseFloat(dims.length) || 0
  var w = parseFloat(dims.width) || 0
  switch (shape) {
    case 'rectangle': return l * w
    case 'l_shape':
      var l2 = parseFloat(dims.length2) || 0
      var w2 = parseFloat(dims.width2) || 0
      return (l * w) + (l2 * w2) - (Math.min(l, l2) * Math.min(w, w2))
    case 'oval': return Math.PI * (l / 2) * (w / 2)
    case 'grecian': return l * w * 0.9
    case 'freeform': return parseFloat(dims.area) || 0
    default: return l * w
  }
}

function estimateVermBags(shape, dims) {
  var l = parseFloat(dims.length) || 0
  var w = parseFloat(dims.width) || 0

  if (shape === 'rectangle' || shape === 'grecian') {
    var sizeKey = Math.round(w) + 'x' + Math.round(l)
    var sizeKeyAlt = Math.round(l) + 'x' + Math.round(w)
    var exact = VERM_RECT.find(function(v) { return v.size === sizeKey || v.size === sizeKeyAlt })
    if (exact) return exact.bags
    var area = l * w
    var sorted = VERM_RECT.map(function(v) {
      var parts = v.size.split('x')
      return { bags: v.bags, area: parseInt(parts[0]) * parseInt(parts[1]) }
    }).sort(function(a, b) { return a.area - b.area })
    for (var i = 0; i < sorted.length - 1; i++) {
      if (area >= sorted[i].area && area <= sorted[i + 1].area) {
        var ratio = (area - sorted[i].area) / (sorted[i + 1].area - sorted[i].area)
        return Math.ceil(sorted[i].bags + ratio * (sorted[i + 1].bags - sorted[i].bags))
      }
    }
    return Math.ceil(area * 0.085)
  }

  if (shape === 'l_shape') {
    var l2val = parseFloat(dims.length2) || 0
    var w2val = parseFloat(dims.width2) || 0
    var key = Math.round(w) + 'x' + Math.round(l) + 'x' + Math.round(l2val || w2val)
    var match = VERM_LSHAPE.find(function(v) { return v.size === key })
    if (match) return match.bags
    var totalArea = calcArea('l_shape', dims)
    return Math.ceil(totalArea * 0.085)
  }

  var poolArea = calcArea(shape, dims)
  return Math.ceil(poolArea * 0.085)
}

// initial: the bid's saved calc_inputs, so reopening the calculator restores it.
// onApply receives { lines, fields, calcInputs, jobName, overview }.
export default function IngroundCalculator({ onApply, onClose, initial }) {
  var init = initial || {}
  var [shape, setShape] = useState(init.shape || 'rectangle')
  var [dims, setDims] = useState(init.dims || { length: '', width: '', length2: '', width2: '', perimeter: '', area: '' })
  var [inputs, setInputs] = useState(init.inputs || {
    plumbing_feet: '',
    water_cost: '',
    crew_hours: '',
    deep_end_yards: '',
    verm_bags_override: '',
    backfill_type: 'sand',
  })
  var [addons, setAddons] = useState(init.addons || {
    stairs: false, stairs_cost: '',
    sun_shelf: false, sun_shelf_cost: '',
    deep_end: false, deep_end_cost: '',
    auto_cover: false, auto_cover_cost: '',
    heater: false, heater_cost: '',
    custom_items: [],
  })
  var [rates, setRates] = useState(init.rates || {
    footing_per_yard: 200,
    plumbing_per_foot: 4,
    sand_per_yard: 30,
    gravel_per_yard: 50,
    verm_per_bag: 40,
    labor_per_hour: 115,
    excavation_rate: 500,
    excavation_divisor: 10,
    overdig_feet: 2,
    standard_depth_inches: 42,
  })
  var [profitMargin, setProfitMargin] = useState(init.profitMargin != null ? init.profitMargin : 20)
  var [exclusions, setExclusions] = useState(init.exclusions || DEFAULT_EXCLUSIONS.map(function(e) { return { ...e } }))
  var [showRates, setShowRates] = useState(false)

  var setDim = function(k, v) { setDims(function(p) { return { ...p, [k]: v } }) }
  var setInput = function(k, v) { setInputs(function(p) { return { ...p, [k]: v } }) }
  var setRate = function(k, v) { setRates(function(p) { return { ...p, [k]: parseFloat(v) || 0 } }) }
  var toggleAddon = function(k) { setAddons(function(p) { return { ...p, [k]: !p[k] } }) }
  var setAddonCost = function(k, v) { setAddons(function(p) { return { ...p, [k]: v } }) }
  var addCustomItem = function() { setAddons(function(p) { return { ...p, custom_items: [...p.custom_items, { desc: '', cost: '' }] } }) }
  var updateCustom = function(i, field, val) { setAddons(function(p) { var items = [...p.custom_items]; items[i] = { ...items[i], [field]: val }; return { ...p, custom_items: items } }) }
  var removeCustom = function(i) { setAddons(function(p) { return { ...p, custom_items: p.custom_items.filter(function(_, idx) { return idx !== i }) } }) }

  var toggleExclusion = function(i) {
    setExclusions(function(p) { var n = [...p]; n[i] = { ...n[i], included: !n[i].included }; return n })
  }
  var updateExclusion = function(i, field, val) {
    setExclusions(function(p) { var n = [...p]; n[i] = { ...n[i], [field]: val }; return n })
  }
  var addExclusion = function() {
    setExclusions(function(p) { return [...p, { label: '', rate: '', unit: '', included: false, custom_note: '' }] })
  }
  var removeExclusion = function(i) {
    setExclusions(function(p) { return p.filter(function(_, idx) { return idx !== i }) })
  }

  // --- CALCULATIONS ---
  var perimeter = calcPerimeter(shape, dims)
  var area = calcArea(shape, dims)

  var footingYards = perimeter * 2 / 27
  var footingCost = footingYards * rates.footing_per_yard

  var plumbingFeet = parseFloat(inputs.plumbing_feet) || 0
  var plumbingCost = plumbingFeet * rates.plumbing_per_foot

  var waterCost = parseFloat(inputs.water_cost) || 0

  var backfillYards = perimeter * 3 * 2 / 27
  var backfillRate = inputs.backfill_type === 'gravel' ? rates.gravel_per_yard : rates.sand_per_yard
  var backfillCost = backfillYards * backfillRate

  var vermBags = inputs.verm_bags_override ? parseInt(inputs.verm_bags_override) : estimateVermBags(shape, dims)
  var vermCost = vermBags * rates.verm_per_bag

  var crewHours = parseFloat(inputs.crew_hours) || 0
  var laborCost = crewHours * rates.labor_per_hour

  var overdig = rates.overdig_feet
  var excLength = (parseFloat(dims.length) || 0) + (overdig * 2)
  var excWidth = (parseFloat(dims.width) || 0) + (overdig * 2)
  var depthFeet = rates.standard_depth_inches / 12
  var excYards = (excLength * excWidth * depthFeet) / 27
  var deepEndYards = parseFloat(inputs.deep_end_yards) || 0
  var totalExcYards = excYards + deepEndYards
  var excavationCost = (totalExcYards / rates.excavation_divisor) * rates.excavation_rate

  var stairsCost = addons.stairs ? (parseFloat(addons.stairs_cost) || 0) : 0
  var sunShelfCost = addons.sun_shelf ? (parseFloat(addons.sun_shelf_cost) || 0) : 0
  var deepEndCost = addons.deep_end ? (parseFloat(addons.deep_end_cost) || 0) : 0
  var autoCoverCost = addons.auto_cover ? (parseFloat(addons.auto_cover_cost) || 0) : 0
  var heaterCost = addons.heater ? (parseFloat(addons.heater_cost) || 0) : 0
  var customTotal = addons.custom_items.reduce(function(s, c) { return s + (parseFloat(c.cost) || 0) }, 0)
  var addonTotal = stairsCost + sunShelfCost + deepEndCost + autoCoverCost + heaterCost + customTotal

  var hardCost = footingCost + plumbingCost + waterCost + backfillCost + vermCost + laborCost + excavationCost + addonTotal
  var marginPct = parseFloat(profitMargin) || 0
  var profitAmount = hardCost * (marginPct / 100)
  var grandTotal = hardCost + profitAmount

  // Included items with a rate and quantity become priced site-work lines.
  var siteWorkTotal = exclusions.reduce(function(sum, e) {
    return sum + (e.included ? (parseFloat(e.rate) || 0) * (parseFloat(e.qty) || 0) : 0)
  }, 0)

  // The customer sees one turnkey line (price in total_cost, hard cost in
  // actual_cost) plus the site-work lines; the cost breakdown stays in
  // calc_inputs so it can be reopened here.
  var handleApply = function() {
    var calcInputs = { shape: shape, dims: dims, inputs: inputs, addons: addons, rates: rates, profitMargin: profitMargin, exclusions: exclusions }
    var built = buildIngroundProposal({
      shape: shape, dims: dims, inputs: inputs, addons: addons, rates: rates, exclusions: exclusions,
      costs: { footing: footingCost, plumbing: plumbingCost, water: waterCost, backfill: backfillCost, verm: vermCost, labor: laborCost, excavation: excavationCost },
      hardCost: hardCost, price: grandTotal,
    })
    calcInputs.breakdown = {
      footing: footingCost, plumbing: plumbingCost, water: waterCost, backfill: backfillCost, verm: vermCost, labor: laborCost,
      excavation: excavationCost, addons: addonTotal, hard_cost: hardCost, profit: profitAmount, price: grandTotal, site_work: siteWorkTotal,
    }
    onApply({ lines: built.lines, fields: built.fields, calcInputs: calcInputs, jobName: built.jobName, overview: built.overview })
  }

  var MULT = String.fromCodePoint(0x00D7)

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '850px' }} onClick={function(e) { e.stopPropagation() }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>INGROUND POOL CALCULATOR</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={function() { setShowRates(!showRates) }} style={s.rateToggle}>{showRates ? 'HIDE RATES' : 'EDIT RATES'}</button>
            <button onClick={onClose} style={s.closeBtn}>X</button>
          </div>
        </div>

        {/* Editable Rates */}
        {showRates && (
          <div style={s.ratesPanel}>
            <div style={s.ratesGrid}>
              <RateField label="FOOTING $/YD" value={rates.footing_per_yard} onChange={function(v) { setRate('footing_per_yard', v) }} />
              <RateField label="PLUMBING $/FT" value={rates.plumbing_per_foot} onChange={function(v) { setRate('plumbing_per_foot', v) }} />
              <RateField label="SAND $/YD" value={rates.sand_per_yard} onChange={function(v) { setRate('sand_per_yard', v) }} />
              <RateField label="GRAVEL $/YD" value={rates.gravel_per_yard} onChange={function(v) { setRate('gravel_per_yard', v) }} />
              <RateField label="VERM $/BAG" value={rates.verm_per_bag} onChange={function(v) { setRate('verm_per_bag', v) }} />
              <RateField label="LABOR $/HR" value={rates.labor_per_hour} onChange={function(v) { setRate('labor_per_hour', v) }} />
              <RateField label="EXCAVATION $" value={rates.excavation_rate} onChange={function(v) { setRate('excavation_rate', v) }} />
              <RateField label="EXC DIVISOR" value={rates.excavation_divisor} onChange={function(v) { setRate('excavation_divisor', v) }} />
              <RateField label="OVERDIG (FT)" value={rates.overdig_feet} onChange={function(v) { setRate('overdig_feet', v) }} />
              <RateField label="DEPTH (IN)" value={rates.standard_depth_inches} onChange={function(v) { setRate('standard_depth_inches', v) }} />
            </div>
          </div>
        )}

        {/* Shape Selection */}
        <div style={s.shapeRow}>
          {SHAPES.map(function(sh) {
            return <button key={sh} onClick={function() { setShape(sh) }} style={{ ...s.shapeBtn, ...(shape === sh ? s.shapeBtnActive : {}) }}>{SHAPE_LABELS[sh]}</button>
          })}
        </div>

        {/* Dimensions */}
        <div style={s.sectionLabel}>POOL DIMENSIONS (feet)</div>
        <div style={s.dimGrid}>
          {shape !== 'freeform' && <>
            <DimField label="LENGTH" value={dims.length} onChange={function(v) { setDim('length', v) }} />
            <DimField label="WIDTH" value={dims.width} onChange={function(v) { setDim('width', v) }} />
          </>}
          {shape === 'l_shape' && <>
            <DimField label="EXTENSION LENGTH" value={dims.length2} onChange={function(v) { setDim('length2', v) }} />
            <DimField label="EXTENSION WIDTH" value={dims.width2} onChange={function(v) { setDim('width2', v) }} />
          </>}
          {shape === 'freeform' && <>
            <DimField label="PERIMETER (ft)" value={dims.perimeter} onChange={function(v) { setDim('perimeter', v) }} />
            <DimField label="SURFACE AREA (sqft)" value={dims.area} onChange={function(v) { setDim('area', v) }} />
            <DimField label="LENGTH (for excavation)" value={dims.length} onChange={function(v) { setDim('length', v) }} />
            <DimField label="WIDTH (for excavation)" value={dims.width} onChange={function(v) { setDim('width', v) }} />
          </>}
        </div>

        {perimeter > 0 && (
          <div style={s.calcInfo}>Perimeter: <span style={s.calcHL}>{perimeter.toFixed(1)} ft</span> | Area: <span style={s.calcHL}>{area.toFixed(0)} sqft</span></div>
        )}

        {/* Inputs */}
        <div style={s.sectionLabel}>JOB INPUTS</div>
        <div style={s.inputGrid}>
          <DimField label="PLUMBING FOOTAGE" value={inputs.plumbing_feet} onChange={function(v) { setInput('plumbing_feet', v) }} />
          <DimField label="WATER COST ($)" value={inputs.water_cost} onChange={function(v) { setInput('water_cost', v) }} />
          <DimField label="CREW HOURS" value={inputs.crew_hours} onChange={function(v) { setInput('crew_hours', v) }} />
          <DimField label="DEEP END EXTRA YARDS" value={inputs.deep_end_yards} onChange={function(v) { setInput('deep_end_yards', v) }} />
          <div>
            <div style={s.fieldLabel}>BACKFILL TYPE</div>
            <div style={s.backfillRow}>
              <button onClick={function() { setInput('backfill_type', 'sand') }} style={{ ...s.bfBtn, ...(inputs.backfill_type === 'sand' ? s.bfBtnActive : {}) }}>SAND (${rates.sand_per_yard}/yd)</button>
              <button onClick={function() { setInput('backfill_type', 'gravel') }} style={{ ...s.bfBtn, ...(inputs.backfill_type === 'gravel' ? s.bfBtnActive : {}) }}>GRAVEL (${rates.gravel_per_yard}/yd)</button>
            </div>
          </div>
          <div>
            <div style={s.fieldLabel}>VERMICULITE BAGS</div>
            <div style={s.vermRow}>
              <span style={s.vermAuto}>Auto: {estimateVermBags(shape, dims)} bags</span>
              <input type="number" value={inputs.verm_bags_override} onChange={function(e) { setInput('verm_bags_override', e.target.value) }} style={{ ...s.dimInput, width: '80px' }} placeholder="Override" min="0" />
            </div>
          </div>
        </div>

        {/* Profit Margin */}
        <div style={s.sectionLabel}>PROFIT MARGIN</div>
        <div style={s.marginRow}>
          <div style={s.marginSlider}>
            {[0, 10, 15, 20, 25, 30, 35, 40, 50].map(function(pct) {
              return <button key={pct} onClick={function() { setProfitMargin(pct) }} style={{ ...s.marginBtn, ...(profitMargin === pct ? s.marginBtnActive : {}) }}>{pct}%</button>
            })}
          </div>
          <div style={s.marginCustom}>
            <span style={s.fieldLabel}>CUSTOM %</span>
            <input type="number" value={profitMargin} onChange={function(e) { setProfitMargin(parseFloat(e.target.value) || 0) }} style={{ ...s.dimInput, width: '70px', textAlign: 'center' }} min="0" max="100" step="1" />
          </div>
          <div style={s.marginInfo}>
            <div style={s.marginInfoLabel}>HARD COST</div>
            <div style={s.marginInfoValue}>{fmtMoney(hardCost)}</div>
          </div>
          <div style={s.marginInfo}>
            <div style={s.marginInfoLabel}>PROFIT (+{marginPct}%)</div>
            <div style={{ ...s.marginInfoValue, color: '#4a8c6a' }}>{fmtMoney(profitAmount)}</div>
          </div>
          <div style={s.marginInfo}>
            <div style={s.marginInfoLabel}>QUOTE PRICE</div>
            <div style={{ ...s.marginInfoValue, color: '#b48c3c', fontSize: '20px' }}>{fmtMoney(grandTotal)}</div>
          </div>
        </div>

        {/* Add-ons */}
        <div style={s.sectionLabel}>ADD-ONS</div>
        <div style={s.addonGrid}>
          {[
            { key: 'stairs', label: 'STAIRS', costKey: 'stairs_cost' },
            { key: 'sun_shelf', label: 'SUN SHELF', costKey: 'sun_shelf_cost' },
            { key: 'deep_end', label: 'DEEP END', costKey: 'deep_end_cost' },
            { key: 'auto_cover', label: 'AUTO COVER', costKey: 'auto_cover_cost' },
            { key: 'heater', label: 'HEATER', costKey: 'heater_cost' },
          ].map(function(addon) {
            return (
              <div key={addon.key} style={s.addonItem}>
                <button onClick={function() { toggleAddon(addon.key) }} style={{ ...s.addonToggle, ...(addons[addon.key] ? s.addonToggleActive : {}) }}>
                  <div style={{ ...s.addonDot, ...(addons[addon.key] ? s.addonDotActive : {}) }} />
                  <span>{addon.label}</span>
                </button>
                {addons[addon.key] && (
                  <div style={s.addonCostWrap}>
                    <span style={s.addonDollar}>$</span>
                    <input type="number" value={addons[addon.costKey]} onChange={function(e) { setAddonCost(addon.costKey, e.target.value) }} style={s.addonCostInput} placeholder="0" step="0.01" />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Custom Items */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', marginBottom: '6px' }}>
          <div style={s.fieldLabel}>CUSTOM ITEMS</div>
          <button onClick={addCustomItem} style={s.addCustomBtn}>+ ADD ITEM</button>
        </div>
        {addons.custom_items.map(function(item, i) {
          return (
            <div key={i} style={s.customRow}>
              <input value={item.desc} onChange={function(e) { updateCustom(i, 'desc', e.target.value) }} style={{ ...s.dimInput, flex: 2 }} placeholder="Description" />
              <div style={s.addonCostWrap}><span style={s.addonDollar}>$</span><input type="number" value={item.cost} onChange={function(e) { updateCustom(i, 'cost', e.target.value) }} style={s.addonCostInput} placeholder="0" step="0.01" /></div>
              <button onClick={function() { removeCustom(i) }} style={s.removeBtn}>X</button>
            </div>
          )
        })}

        {/* Inclusions / Exclusions */}
        <div style={s.sectionLabel}>INCLUDED / NOT INCLUDED</div>
        <div style={s.exclDesc}>Toggle items to mark as INCLUDED (green) or NOT INCLUDED (red). Included items with a rate and quantity become priced site-work lines; the rest go in the Not Included list.</div>
        <div style={s.exclGrid}>
          {exclusions.map(function(item, i) {
            return (
              <div key={i} style={{ ...s.exclRow, borderLeft: '3px solid ' + (item.included ? '#4a8c6a' : '#e05c5c') }}>
                <button onClick={function() { toggleExclusion(i) }} style={{ ...s.exclToggle, backgroundColor: item.included ? 'rgba(74,140,106,0.1)' : 'rgba(224,92,92,0.06)' }}>
                  <span style={{ color: item.included ? '#4a8c6a' : '#e05c5c', fontWeight: 700, fontSize: '11px' }}>{item.included ? 'INCLUDED' : 'NOT INCL'}</span>
                </button>
                <input value={item.label} onChange={function(e) { updateExclusion(i, 'label', e.target.value) }} style={{ ...s.dimInput, flex: 2, fontSize: '13px', padding: '8px' }} placeholder="Item name" />
                {item.included && <input type="number" value={item.qty || ''} onChange={function(e) { updateExclusion(i, 'qty', e.target.value) }} style={{ ...s.dimInput, width: '70px', fontSize: '13px', padding: '8px', textAlign: 'right' }} placeholder="qty" min="0" step="any" />}
                <input value={item.rate} onChange={function(e) { updateExclusion(i, 'rate', e.target.value) }} style={{ ...s.dimInput, width: '70px', fontSize: '13px', padding: '8px', textAlign: 'right' }} placeholder="$" />
                <input value={item.unit} onChange={function(e) { updateExclusion(i, 'unit', e.target.value) }} style={{ ...s.dimInput, width: '70px', fontSize: '12px', padding: '8px' }} placeholder="unit" />
                <input value={item.custom_note} onChange={function(e) { updateExclusion(i, 'custom_note', e.target.value) }} style={{ ...s.dimInput, flex: 1, fontSize: '12px', padding: '8px' }} placeholder="Note" />
                {i >= DEFAULT_EXCLUSIONS.length && <button onClick={function() { removeExclusion(i) }} style={s.removeBtn}>X</button>}
              </div>
            )
          })}
        </div>
        <button onClick={addExclusion} style={{ ...s.addCustomBtn, marginTop: '8px' }}>+ ADD ITEM</button>

        {/* Results */}
        <div style={s.resultsPanel}>
          <div style={s.resultTitle}>COST BREAKDOWN</div>
          {footingCost > 0 && <ResultRow label={'Footing (' + footingYards.toFixed(1) + ' yds ' + MULT + ' $' + rates.footing_per_yard + ')'} value={footingCost} />}
          {plumbingCost > 0 && <ResultRow label={'Plumbing (' + plumbingFeet + ' ft ' + MULT + ' $' + rates.plumbing_per_foot + ')'} value={plumbingCost} />}
          {waterCost > 0 && <ResultRow label="Water Cost" value={waterCost} />}
          {backfillCost > 0 && <ResultRow label={'Backfill ' + (inputs.backfill_type === 'gravel' ? 'Gravel' : 'Sand') + ' (' + backfillYards.toFixed(1) + ' yds ' + MULT + ' $' + backfillRate + ')'} value={backfillCost} />}
          {vermCost > 0 && <ResultRow label={'Vermiculite (' + vermBags + ' bags ' + MULT + ' $' + rates.verm_per_bag + ')'} value={vermCost} />}
          {laborCost > 0 && <ResultRow label={'Labor (' + crewHours + ' hrs ' + MULT + ' $' + rates.labor_per_hour + ')'} value={laborCost} />}
          {excavationCost > 0 && <div>
            <ResultRow label={'Excavation (' + totalExcYards.toFixed(1) + ' yds / ' + rates.excavation_divisor + ' ' + MULT + ' $' + rates.excavation_rate + ')'} value={excavationCost} />
            <div style={s.excDetail}>{excLength.toFixed(0)}' x {excWidth.toFixed(0)}' x {depthFeet.toFixed(1)}' = {excYards.toFixed(1)} yds{deepEndYards > 0 ? ' + ' + deepEndYards + ' deep end' : ''}</div>
          </div>}
          {addonTotal > 0 && <div>
            <div style={s.resultDivider} />
            {stairsCost > 0 && <ResultRow label="Stairs" value={stairsCost} />}
            {sunShelfCost > 0 && <ResultRow label="Sun Shelf" value={sunShelfCost} />}
            {deepEndCost > 0 && <ResultRow label="Deep End" value={deepEndCost} />}
            {autoCoverCost > 0 && <ResultRow label="Auto Cover" value={autoCoverCost} />}
            {heaterCost > 0 && <ResultRow label="Heater" value={heaterCost} />}
            {addons.custom_items.map(function(c, i) { return parseFloat(c.cost) > 0 ? <ResultRow key={i} label={c.desc || 'Custom'} value={parseFloat(c.cost)} /> : null })}
          </div>}
          <div style={s.resultDivider} />
          <ResultRow label="HARD COST SUBTOTAL" value={hardCost} />
          {profitAmount > 0 && <ResultRow label={'PROFIT MARGIN (' + marginPct + '%)'} value={profitAmount} color="#4a8c6a" />}
          <div style={s.resultDivider} />
          <div style={s.totalRow}>
            <span>POOL CONSTRUCTION (TURNKEY)</span>
            <span style={s.totalValue}>{fmtMoney(grandTotal)}</span>
          </div>
          {siteWorkTotal > 0 && <ResultRow label="SITE WORK (INCLUDED ITEMS)" value={siteWorkTotal} />}
          {siteWorkTotal > 0 && <ResultRow label="PROJECT TOTAL" value={grandTotal + siteWorkTotal} color="#b48c3c" />}
          <div style={s.paymentTerms}>Payment: pool kit at order, materials deposit at install, balance less retainer at water fill. Set the kit cost in the proposal section of the bid.</div>
        </div>

        <button onClick={handleApply} disabled={grandTotal === 0} style={{ ...s.applyBtn, opacity: grandTotal > 0 ? 1 : 0.4 }}>
          APPLY TO PROPOSAL
        </button>
      </div>
    </div>
  )
}

function DimField({ label, value, onChange }) {
  return <div><div style={s.fieldLabel}>{label}</div><input type="number" value={value} onChange={function(e) { onChange(e.target.value) }} style={s.dimInput} placeholder="0" min="0" step="any" /></div>
}

function RateField({ label, value, onChange }) {
  return <div><div style={s.rateLabel}>{label}</div><input type="number" value={value} onChange={function(e) { onChange(e.target.value) }} style={s.rateInput} step="any" /></div>
}

function ResultRow({ label, value, color }) {
  return <div style={s.resultRow}><span style={s.resultLabel}>{label}</span><span style={{ ...s.resultValue, color: color || '#ffffff' }}>{fmtMoney(value)}</span></div>
}

var s = {
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '32px', width: '100%', maxHeight: '90vh', overflow: 'auto', fontFamily: "'Barlow', sans-serif" },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '20px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c' },
  closeBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontSize: '14px', padding: '6px 10px', cursor: 'pointer' },
  rateToggle: { background: 'none', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', padding: '6px 12px', cursor: 'pointer' },
  ratesPanel: { backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', padding: '16px', marginBottom: '20px' },
  ratesGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' },
  rateLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginBottom: '4px' },
  rateInput: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', textAlign: 'center', outline: 'none', boxSizing: 'border-box', fontFamily: "'Barlow', sans-serif" },
  shapeRow: { display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' },
  shapeBtn: { padding: '10px 18px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },
  shapeBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  sectionLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '10px', marginTop: '16px' },
  dimGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '8px' },
  inputGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '8px' },
  fieldLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginBottom: '4px' },
  dimInput: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  calcInfo: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' },
  calcHL: { color: '#b48c3c', fontWeight: 600 },
  backfillRow: { display: 'flex', gap: '6px' },
  bfBtn: { flex: 1, padding: '8px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  bfBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  vermRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  vermAuto: { fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  // Profit margin
  marginRow: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '16px', backgroundColor: 'rgba(74,140,106,0.06)', border: '1px solid rgba(74,140,106,0.15)', borderRadius: '4px', marginBottom: '8px' },
  marginSlider: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  marginBtn: { padding: '6px 10px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, cursor: 'pointer', minWidth: '40px', textAlign: 'center' },
  marginBtnActive: { backgroundColor: 'rgba(74,140,106,0.2)', borderColor: 'rgba(74,140,106,0.5)', color: '#4a8c6a' },
  marginCustom: { display: 'flex', alignItems: 'center', gap: '6px' },
  marginInfo: { textAlign: 'center' },
  marginInfoLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)' },
  marginInfoValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, color: '#ffffff', marginTop: '2px' },
  // Add-ons
  addonGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' },
  addonItem: {},
  addonToggle: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  addonToggleActive: { backgroundColor: 'rgba(180,140,60,0.12)', borderColor: 'rgba(180,140,60,0.35)', color: '#ffffff' },
  addonDot: { width: '10px', height: '10px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 },
  addonDotActive: { backgroundColor: '#b48c3c', borderColor: '#b48c3c' },
  addonCostWrap: { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' },
  addonDollar: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  addonCostInput: { flex: 1, padding: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box' },
  addCustomBtn: { background: 'none', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', padding: '5px 12px', cursor: 'pointer' },
  customRow: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' },
  removeBtn: { background: 'none', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px', padding: '6px 10px', cursor: 'pointer' },
  // Exclusions
  exclDesc: { fontSize: '12px', color: 'rgba(255,255,255,0.25)', marginBottom: '10px' },
  exclGrid: { display: 'flex', flexDirection: 'column', gap: '4px' },
  exclRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '3px' },
  exclToggle: { padding: '6px 10px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', cursor: 'pointer', minWidth: '85px', textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif" },
  // Results
  resultsPanel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', marginTop: '20px', marginBottom: '16px' },
  resultTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '14px' },
  resultRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '13px' },
  resultLabel: { color: 'rgba(255,255,255,0.5)' },
  resultValue: { color: '#ffffff', fontWeight: 600 },
  resultDivider: { height: '1px', backgroundColor: 'rgba(255,255,255,0.06)', margin: '10px 0' },
  excDetail: { fontSize: '11px', color: 'rgba(255,255,255,0.25)', paddingLeft: '8px', marginTop: '2px' },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '20px', fontWeight: 700 },
  totalValue: { color: '#b48c3c', fontSize: '28px' },
  paymentTerms: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '12px', fontStyle: 'italic', textAlign: 'center' },
  applyBtn: { width: '100%', padding: '16px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '4px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '3px', cursor: 'pointer' },
}
